import React, {
  forwardRef,
  type Ref,
  Suspense,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Spinner } from "../Progress/Spinner";
import { TabPane, type TabPaneProps } from "./TabPane";
import { TabsBar } from "./TabsBar";

export interface TabsMethods {
  changeTab: (index: number) => void;
}

export interface TabsProps {
  children: React.ReactNode;
  defaultActiveKey?: string;
  defer?: boolean;
  onChange?: (index: number) => void;
}

const TabsComponent = (
  { children, defaultActiveKey, defer, onChange }: TabsProps,
  ref: Ref<TabsMethods>,
) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const { hash } = globalThis.location;

  const handleTabChange = (index: number) => {
    if (index !== activeTab) {
      setActiveTab(index);
      onChange?.(index);
    }
  };

  useImperativeHandle(ref, () => ({
    changeTab: (index: number) => {
      setActiveTab(index);
    },
  }));

  const activeKey = hash?.replace("#", "") || defaultActiveKey;

  const tabsOnly = React.Children.toArray(children).filter((child) => {
    return React.isValidElement(child) && child.type === TabPane;
  });

  useEffect(() => {
    React.Children.map(tabsOnly, (child, index) => {
      if (
        React.isValidElement<TabPaneProps>(child) &&
        activeKey &&
        child?.props.tab === decodeURIComponent(activeKey)
      ) {
        setActiveTab(index);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex w-full">
      <div className="grow">
        <TabsBar activeIndex={activeTab} onTabChange={handleTabChange}>
          {tabsOnly}
        </TabsBar>

        {defer
          ? React.Children.map(tabsOnly, (child, index) =>
              React.isValidElement<TabPaneProps>(child) &&
              index === activeTab ? (
                <div className="animate-neru-fade-in-fast my-4 opacity-0">
                  <Suspense fallback={<Spinner />}>
                    {child.props.children}
                  </Suspense>
                </div>
              ) : null,
            )
          : React.Children.map(tabsOnly, (child, index) =>
              React.isValidElement<TabPaneProps>(child) ? (
                <div
                  className={`
                    dark:text-primary-0
                    animate-neru-fade-in-fast my-4 opacity-0
                  `}
                  style={{
                    display: `${index === activeTab ? "block" : "none"}`,
                  }}
                >
                  {child.props.children}
                </div>
              ) : null,
            )}
      </div>
    </div>
  );
};

export const Tabs = forwardRef(TabsComponent);
